import React from "react";
// Review-specific components (duplicated for review-only display)
import {
  PairingReview,
  SingleCorrectAnswerReview,
  MultipleCorrectAnswerReview,
  FillInTheBlankReview,
  SequencingReview,
  PassageReview,
} from "./ReviewComponents/QuickPractice";
import {
  SpeakingReview,
  PronunciationReview,
} from "./ReviewComponents/SpeakingPractice";
import {
  ShortAnswerReview,
  EssayReview,
  QuestionAndAnswerReview,
} from "./ReviewComponents/WritingPractice";
import { ReviewQuestionHeader } from "./ReviewComponents/shared/ReviewQuestionHeader";

// changed by Cursor: tambah currentQuestionNumber & totalQuestions untuk distinct question label
export function ReviewContent({ question, courseType = "quick", langCode = null, currentQuestionNumber = null, totalQuestions = null }) {
  if (!question) {
    return (
      <div className="review-content flex-1 bg-[var(--card-bg)] p-4 rounded-2xl shadow-[var(--shadow-md)] dark:border dark:border-white/10 max-xl:pb-[100px] max-xl:px-2 xl:p-8" style={{ pointerEvents: "auto" }}>
        <div className="flex items-center justify-center h-[400px] text-[var(--text-tertiary)] text-base">
          <p>No question data available</p>
        </div>
      </div>
    );
  }

  const isCorrect = question.isCorrect;
  // Get question type from multiple sources - prioritize originalQuestion.type
  const originalQuestion = question.originalQuestion || {};
  const questionType = originalQuestion.type || question.type || "Question";

  // Extract data sesuai REVIEW_PAGE_FLOW.md
  // 1. Question display dari originalQuestion (GET API)
  // Note: originalQuestion sudah didefinisikan di atas

  // 2. User answer (dari userAnswers array)
  // For pronunciation/speaking, keep object structure with assessment
  let userAnswer;
  if (questionType === "pronunciation" || questionType === "speaking") {
    // Check if question.userAnswer is object with assessment field
    if (
      typeof question.userAnswer === "object" &&
      !Array.isArray(question.userAnswer)
    ) {
      userAnswer = question.userAnswer; // Keep full object {assessment, audioUrl, userAnswer}
      if (process.env.NODE_ENV === "development") {
        // console.log('🔍 ReviewContent - Keeping object for pronunciation:', {
        //   userAnswerKeys: Object.keys(userAnswer),
        //   hasAssessment: !!userAnswer.assessment,
        // });
      }
    } else {
      // CRITICAL: Jangan gunakan || [] karena 0 adalah falsy tapi valid answer
      userAnswer =
        question.userAnswer !== undefined && question.userAnswer !== null
          ? question.userAnswer
          : [];
    }
  } else {
    // CRITICAL: Jangan gunakan || [] karena 0 adalah falsy tapi valid answer
    // Untuk single correct answer, userAnswer bisa jadi number (idx) atau array
    userAnswer =
      question.userAnswer !== undefined && question.userAnswer !== null
        ? question.userAnswer
        : [];
  }

  // 3. Correct answer (SELALU dari GET API, bukan dari SUBMIT)
  const correctAnswer =
    question.correctAnswer || originalQuestion.answers || [];

  // 4. Accuracy (dari SUBMIT API soals-stats)
  const accuracy = question.accuracy || (isCorrect ? 1.0 : 0.0);

  // 5. Explanation (dari question.explanation atau originalQuestion.explanation)
  const explanation =
    question.explanation || originalQuestion.explanation || "";

  // 6. Recap dan Annotations (dari originalQuestion.recap dan originalQuestion.annotations)
  const recap = originalQuestion.recap || question.recap || "";
  const annotations =
    originalQuestion.annotations || question.annotations || [];

  // Map question type to component
  const mapQuestionType = (backendType) => {
    const typeMap = {
      sequencing: "sequencing",
      pairing: "pairing",
      "pilgan-single": "sc",
      "pilgan-multi": "mc",
      "pilgan-multiple": "mc",
      fib: "fib",
      "fill-blank": "fib",
      "fill-in-the-blank": "fib",
      passage: "passage",
      speaking: "speaking",
      pronunciation: "pronunciation",
      pronun: "pronunciation",
      shorts: "shorts",
      "short-answer": "shorts",
      essay: "essay",
      "optionless-fib": "fib",
      qna: "qna",
      "question-answer": "qna",
    };
    return typeMap[backendType] || backendType;
  };

  // Map question type to display name (user-friendly)
  const getQuestionTypeDisplayName = (backendType) => {
    const displayMap = {
      "pilgan-single": "Single Correct Answer",
      "pilgan-multi": "Multiple Correct Answer",
      "pilgan-multiple": "Multiple Correct Answer",
      fib: "Fill in the Blank",
      "fill-blank": "Fill in the Blank",
      "fill-in-the-blank": "Fill in the Blank",
      sequencing: "Sequencing",
      pairing: "Pairing",
      passage: "Passage",
      speaking: "Speaking",
      pronunciation: "Pronunciation",
      shorts: "Short Answer",
      "short-answer": "Short Answer",
      essay: "Essay",
      "optionless-fib": "Fill in the Blank",
      qna: "Question and Answer",
      "question-answer": "Question and Answer",
    };
    return displayMap[backendType] || backendType;
  };

  // Hide header for all review types because all review components already have their own headers
  // Quick practice review components: passage, sc, mc, fib, sequencing, pairing
  // Writing review components: essay, shorts, qna
  // Speaking review components: speaking, pronunciation
  const quickPracticeTypes = [
    "passage",
    "sc",
    "mc",
    "fib",
    "sequencing",
    "pairing",
  ];
  const writingPracticeTypes = ["essay", "shorts", "qna"];
  const speakingPracticeTypes = ["speaking", "pronunciation"];

  const componentType = mapQuestionType(questionType);
  const shouldHideHeader =
    quickPracticeTypes.includes(componentType) ||
    writingPracticeTypes.includes(componentType) ||
    speakingPracticeTypes.includes(componentType);

  // Report button props - passed to content box header (top right, aligned with title)
  const reportType = (courseType || "").toLowerCase().includes("quick")
    ? "quick"
    : (courseType || "").toLowerCase().includes("writing")
    ? "writing"
    : (courseType || "").toLowerCase().includes("speaking")
    ? "speaking"
    : "quick";
  const reportProps = {
    prosetId: originalQuestion["proset-id"] || originalQuestion.prosetId || originalQuestion.proset_id || question?.prosetId || null,
    soalId: originalQuestion["soal-id"] || originalQuestion.soalId || originalQuestion.id || question?.soalId || question?.id || null,
    reportType,
    currentQuestionNumber: currentQuestionNumber ?? (question?.questionIndex !== undefined ? question.questionIndex + 1 : 1),
    totalQuestions: totalQuestions ?? 1,
    score: Math.round((accuracy ?? (isCorrect ? 1 : 0)) * 100),
  };

  // Render question component in review mode (read-only with answer highlighting)
  const renderQuestionComponent = () => {
    const questionId =
      originalQuestion["soal-id"] || originalQuestion.id || question.prompt;

    // For pairing, ensure correctAnswer is in correct format (array of {left, right} objects)
    // CRITICAL: Correct answer SELALU dari GET API (originalQuestion.answers atau pairs)
    let formattedCorrectAnswer = correctAnswer;
    if (componentType === "pairing") {
      // Priority 1: Build dari originalQuestion.pairs (GET API)
      // Pairs dibangun dari left dan right items dengan idx yang sama
      if (
        originalQuestion.pairs &&
        originalQuestion.pairs.left &&
        originalQuestion.pairs.right
      ) {
        const pairs = [];
        originalQuestion.pairs.left.forEach((leftItem) => {
          const leftIdx = leftItem.idx ?? leftItem.id;
          // Find matching right item dengan idx yang sama
          const rightItem = originalQuestion.pairs.right.find(
            (r) => (r.idx ?? r.id) === leftIdx
          );
          if (rightItem) {
            const rightIdx = rightItem.idx ?? rightItem.id;
            pairs.push({
              left: leftIdx,
              right: rightIdx,
            });
          }
        });
        if (pairs.length > 0) {
          formattedCorrectAnswer = pairs;
        }
      }
      // Priority 2: Gunakan originalQuestion.answers jika ada (GET API)
      else if (
        originalQuestion.answers &&
        Array.isArray(originalQuestion.answers)
      ) {
        // Check if it's already array of {left, right} objects
        if (
          originalQuestion.answers[0] &&
          typeof originalQuestion.answers[0] === "object" &&
          originalQuestion.answers[0].hasOwnProperty("left")
        ) {
          formattedCorrectAnswer = originalQuestion.answers;
        }
      }
      // Priority 3: Fallback ke correctAnswer dari prop (jika sudah dalam format yang benar)
      else if (Array.isArray(correctAnswer) && correctAnswer.length > 0) {
        if (
          correctAnswer[0] &&
          typeof correctAnswer[0] === "object" &&
          correctAnswer[0].hasOwnProperty("left")
        ) {
          formattedCorrectAnswer = correctAnswer;
        }
      }
    }

    // Prepare question data for review components
    // changed by Cursor: include passage & translation from API (question level or session level) so passage review can show it
    const reviewQuestion = {
      ...originalQuestion,
      // changed by Cursor: gunakan questionIndex + 1 dari ReviewPage, bukan hardcoded 1
      // Ini penting untuk speaking/pronunciation localStorage audio matching by index
      number: (question.questionIndex !== undefined ? question.questionIndex + 1 : 1),
      translation: question.translation ?? originalQuestion?.translation,
      passage: question.passage ?? originalQuestion?.passage,
      // changed by Cursor: pass originalQuestion sebagai nested field supaya review components
      // (Speaking/Pronunciation) bisa akses semua possible IDs untuk localStorage audio matching
      originalQuestion: originalQuestion,
    };

    switch (componentType) {
      case "pairing":
        // changed by Cursor: tambah fallback localStorage untuk pairing (sama seperti mc/sequencing)
        let finalUserAnswerForPairing = userAnswer;

        // Jika userAnswer kosong atau tidak valid untuk pairing, ambil dari localStorage
        // Valid pairing answer format: {left: [...], right: [...]} atau array of {left, right}
        const isPairingAnswerValid = 
          (finalUserAnswerForPairing?.left && finalUserAnswerForPairing?.right) ||
          (Array.isArray(finalUserAnswerForPairing) && finalUserAnswerForPairing.length > 0);

        if (!isPairingAnswerValid) {
          try {
            const userAnswersRaw = localStorage.getItem("user-answers");
            if (userAnswersRaw) {
              const parsed = JSON.parse(userAnswersRaw);
              const localStorageUserAnswers = parsed["user-answers"] || [];

              // Cari matching entry berdasarkan soal-id atau proset-id
              const soalId =
                originalQuestion["soal-id"] ||
                originalQuestion.soalId ||
                originalQuestion.id;
              const prosetId =
                originalQuestion["proset-id"] ||
                originalQuestion.prosetId ||
                originalQuestion.proset_id;

              const matchingEntry = localStorageUserAnswers.find(
                (ua) =>
                  ua?.["soal-id"] === soalId ||
                  ua?.soalId === soalId ||
                  ua?.["proset-id"] === prosetId ||
                  ua?.prosetId === prosetId
              );

              if (matchingEntry) {
                const answer =
                  matchingEntry["user-answer"] || matchingEntry.userAnswer;
                // Pairing answer bisa berupa object {left, right} atau array
                if (answer && (answer.left || Array.isArray(answer))) {
                  finalUserAnswerForPairing = answer;
                  console.log('✅ Pairing userAnswer loaded from localStorage:', finalUserAnswerForPairing);
                }
              }
            }
          } catch (error) {
            console.error("Error reading pairing userAnswer from localStorage:", error);
          }
        }

        return (
          <PairingReview
            key={questionId}
            question={reviewQuestion}
            userAnswer={finalUserAnswerForPairing}
            correctAnswer={formattedCorrectAnswer}
            isCorrect={isCorrect}
            accuracy={accuracy}
            explanation={explanation}
            recap={recap}
            annotations={annotations}
            reportProps={reportProps}
          />
        );
      case "sc":
        return (
          <SingleCorrectAnswerReview
            key={questionId}
            question={reviewQuestion}
            userAnswer={userAnswer}
            correctAnswer={formattedCorrectAnswer}
            isCorrect={isCorrect}
            accuracy={accuracy}
            explanation={explanation}
            recap={recap}
            annotations={annotations}
            reportProps={reportProps}
          />
        );
      case "mc":
        // CRITICAL: Gunakan userAnswer dari index.jsx jika valid, baru fallback ke localStorage
        let finalUserAnswerForMc = userAnswer;

        // Jika userAnswer dari index.jsx kosong atau tidak valid, ambil dari localStorage
        if (
          !finalUserAnswerForMc ||
          !Array.isArray(finalUserAnswerForMc) ||
          finalUserAnswerForMc.length === 0
        ) {
          try {
            const userAnswersRaw = localStorage.getItem("user-answers");
            if (userAnswersRaw) {
              const parsed = JSON.parse(userAnswersRaw);
              const localStorageUserAnswers = parsed["user-answers"] || [];

              // Cari matching entry berdasarkan soal-id atau proset-id
              const soalId =
                originalQuestion["soal-id"] ||
                originalQuestion.soalId ||
                originalQuestion.id;
              const prosetId =
                originalQuestion["proset-id"] ||
                originalQuestion.prosetId ||
                originalQuestion.proset_id;

              const matchingEntry = localStorageUserAnswers.find(
                (ua) =>
                  ua?.["soal-id"] === soalId ||
                  ua?.soalId === soalId ||
                  ua?.["proset-id"] === prosetId ||
                  ua?.prosetId === prosetId
              );

              if (matchingEntry) {
                const answer =
                  matchingEntry["user-answer"] || matchingEntry.userAnswer;
                if (Array.isArray(answer) && answer.length > 0) {
                  finalUserAnswerForMc = answer;
                }
              }
            }
          } catch (error) {
            console.error(
              "❌ ReviewContent - Error loading from localStorage:",
              error
            );
          }
        }

        return (
          <MultipleCorrectAnswerReview
            key={questionId}
            question={reviewQuestion}
            userAnswer={finalUserAnswerForMc}
            correctAnswer={formattedCorrectAnswer}
            isCorrect={isCorrect}
            accuracy={accuracy}
            explanation={explanation}
            recap={recap}
            annotations={annotations}
            reportProps={reportProps}
          />
        );
      case "fib":
        return (
          <FillInTheBlankReview
            key={questionId}
            question={reviewQuestion}
            userAnswer={userAnswer}
            correctAnswer={formattedCorrectAnswer}
            isCorrect={isCorrect}
            accuracy={accuracy}
            feedback={question.feedback || question.feedbacks}
            explanation={explanation}
            recap={recap}
            annotations={annotations}
            reportProps={reportProps}
          />
        );
      case "sequencing":
        // CRITICAL: Gunakan userAnswer dari index.jsx jika valid, baru fallback ke localStorage
        let finalUserAnswerForSequencing = userAnswer;

        // Jika userAnswer dari index.jsx kosong atau tidak valid, ambil dari localStorage
        if (
          !finalUserAnswerForSequencing ||
          !Array.isArray(finalUserAnswerForSequencing) ||
          finalUserAnswerForSequencing.length === 0
        ) {
          try {
            const userAnswersRaw = localStorage.getItem("user-answers");
            if (userAnswersRaw) {
              const parsed = JSON.parse(userAnswersRaw);
              const localStorageUserAnswers = parsed["user-answers"] || [];

              // Cari matching entry berdasarkan soal-id atau proset-id
              const soalId =
                originalQuestion["soal-id"] ||
                originalQuestion.soalId ||
                originalQuestion.id;
              const prosetId =
                originalQuestion["proset-id"] ||
                originalQuestion.prosetId ||
                originalQuestion.proset_id;

              const matchingEntry = localStorageUserAnswers.find(
                (ua) =>
                  ua?.["soal-id"] === soalId ||
                  ua?.soalId === soalId ||
                  ua?.["proset-id"] === prosetId ||
                  ua?.prosetId === prosetId
              );

              if (matchingEntry) {
                const answer =
                  matchingEntry["user-answer"] || matchingEntry.userAnswer;
                if (Array.isArray(answer) && answer.length > 0) {
                  finalUserAnswerForSequencing = answer;
                }
              }
            }
          } catch (error) {
            console.error(
              "❌ ReviewContent - Error loading sequencing from localStorage:",
              error
            );
          }
        }

        return (
          <SequencingReview
            key={questionId}
            question={reviewQuestion}
            userAnswer={finalUserAnswerForSequencing}
            correctAnswer={formattedCorrectAnswer}
            isCorrect={isCorrect}
            accuracy={accuracy}
            explanation={explanation}
            recap={recap}
            annotations={annotations}
            reportProps={reportProps}
          />
        );
      case "passage":
        return (
          <PassageReview
            key={questionId}
            question={reviewQuestion}
            userAnswer={userAnswer}
            correctAnswer={formattedCorrectAnswer}
            isCorrect={isCorrect}
            accuracy={accuracy}
            explanation={explanation}
            recap={recap}
            annotations={annotations}
            reportProps={reportProps}
          />
        );
      case "speaking":
        return (
          <SpeakingReview
            key={questionId}
            question={reviewQuestion}
            userAnswer={userAnswer}
            isCorrect={isCorrect}
            feedback={question.feedback || question.feedbacks}
            explanation={explanation}
            recap={recap}
            annotations={annotations}
            reportProps={reportProps}
          />
        );
      case "pronunciation":
        return (
          <PronunciationReview
            key={questionId}
            question={reviewQuestion}
            userAnswer={userAnswer}
            isCorrect={isCorrect}
            explanation={explanation}
            recap={recap}
            annotations={annotations}
            langCode={langCode}
            reportProps={reportProps}
          />
        );
      case "shorts":
        return (
          <ShortAnswerReview
            key={questionId}
            question={reviewQuestion}
            userAnswer={userAnswer}
            correctAnswer={formattedCorrectAnswer}
            isCorrect={isCorrect}
            feedback={question.feedback}
            explanation={explanation}
            recap={recap}
            annotations={annotations}
            langCode={langCode}
            reportProps={reportProps}
          />
        );
      case "essay":
        return (
          <EssayReview
            key={questionId}
            question={reviewQuestion}
            userAnswer={userAnswer}
            isCorrect={isCorrect}
            feedback={question.feedback}
            explanation={explanation}
            recap={recap}
            annotations={annotations}
            langCode={langCode}
            reportProps={reportProps}
          />
        );
      case "qna":
        return (
          <QuestionAndAnswerReview
            key={questionId}
            question={reviewQuestion}
            userAnswer={userAnswer}
            correctAnswer={formattedCorrectAnswer}
            isCorrect={isCorrect}
            feedback={question.feedback}
            explanation={explanation}
            recap={recap}
            annotations={annotations}
            langCode={langCode}
            reportProps={reportProps}
          />
        );
      // TODO: Add other types (FIB, Sequencing, Passage, OptionlessFIB) as review components
      default:
        return (
          <div className="bg-[var(--card-bg)] rounded-xl p-4 mb-4 max-xl:py-2 max-xl:px-0 xl:p-6 xl:mb-6">
            <ReviewQuestionHeader questionType={getQuestionTypeDisplayName(questionType)} />
            <div className="flex flex-col gap-5">
              <p className="text-base text-[var(--text-primary)] leading-[1.6]">
                Review component for {getQuestionTypeDisplayName(questionType)}{" "}
                is not yet implemented.
              </p>
            </div>
          </div>
        );
    }
  };

  // For pronunciation and speaking, don't wrap in review-question-display (which disables pointer-events)
  const shouldDisableInteractions =
    componentType !== "pronunciation" && componentType !== "speaking";

  // CRITICAL: Force hide header for qna - check ALL possible sources
  const isQnaComponent =
    componentType === "qna" ||
    questionType === "qna" ||
    questionType === "question-answer" ||
    originalQuestion.type === "qna" ||
    originalQuestion.type === "question-answer" ||
    question.type === "qna" ||
    question.type === "question-answer";

  // NEVER show header if it's QNA - QuestionAndAnswerReview has its own header

  return (
      <div className="review-content flex-1 bg-[var(--card-bg)] p-4 rounded-2xl shadow-[var(--shadow-md)] dark:border dark:border-white/10 max-xl:pb-[100px] max-xl:px-2 xl:p-8" style={{ pointerEvents: "auto" }}>
      {/* CRITICAL: Do NOT render header for QNA - QuestionAndAnswerReview already has header */}
      {!isQnaComponent && !(shouldHideHeader || isQnaComponent) && (
        <div className="flex items-center justify-between mb-5 pb-3 border-b-2 border-b-[var(--border-color)]">
          <h2 className="text-2xl font-bold text-[#146e69] dark:text-[#2dd4bf] m-0 max-xl:text-xl">
            {getQuestionTypeDisplayName(questionType)}
          </h2>
        </div>
      )}

      {/* Render question component (same as practice page) */}
      {shouldDisableInteractions ? (
        <div className="review-question-display">
          {renderQuestionComponent()}
        </div>
      ) : (
        <div style={{ pointerEvents: "auto" }}>{renderQuestionComponent()}</div>
      )}

      {/* changed by Cursor: remove separate Translation card - translation already shown in Text to Read section */}
    </div>
  );
}
